import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Trash2, RotateCcw, Grid3x3, Move } from 'lucide-react';
import { log } from '../../lib/logger';

/**
 * Page-Maker Freeform Layout Editor
 *
 * Drop-in replacement for the auto-grid banner when the customer wants
 * pixel-perfect control. Presents a letter (8.5 × 11") canvas as an SVG
 * with each design rendered as a draggable + resizable tile:
 *   - Tiles seed from the auto-grid on mount, one per available design.
 *   - Drag tile body → reposition. Drag bottom-right corner handle → resize.
 *   - Select a tile to change its shape (rect / rounded / circle) or
 *     delete it. "Add tile" duplicates the selected design at a free spot.
 *   - "Auto-grid" button resets the layout to the default tiling so users
 *     never feel trapped.
 *
 * All coordinates are INCHES from the sheet's TOP-LEFT corner (so the
 * same value maps 1:1 to the page_maker_layout schema the backend
 * consumes). The SVG render scales inches → pixels via a single
 * `pxPerInch` ratio fitted to the container width.
 *
 * Props:
 *   files         – array of File objects the user has uploaded
 *   filePreviews  – array of { idx, thumbUrl } for quick-rendered
 *                   previews (optional; falls back to a coloured swatch)
 *   defaultSize   – { w, h, unit } of the sticker (same values the
 *                   auto-grid uses to seed the initial tile size)
 *   cutlineShape  – order-level cutline shape used as the per-tile
 *                   default when the user hasn't overridden it
 *   onChange      – (layout) => void, fires every time the layout
 *                   changes so the parent can persist it
 */
const PAGE_W_IN = 8.5;
const PAGE_H_IN = 11;
const MARGIN_IN = 0.25;
const HEADER_RESERVE_IN = 0.85;

// Stable random-looking hue for unknown-index tiles — 47° step is coprime
// with 360° so adjacent tiles never share a hue.
const hueFor = (idx) => (((idx || 0) * 47) % 360 + 360) % 360;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const unitToInches = (w, h, unit) => {
  const u = (unit || 'inch').toLowerCase();
  if (u.startsWith('cm')) return [w / 2.54, h / 2.54];
  if (u.startsWith('mm')) return [w / 25.4, h / 25.4];
  return [w, h];
};

/**
 * Build the default auto-grid layout. Tiles round-robin through the
 * available design indices so the editor seeds with "what the grid
 * would have rendered" — making it obvious the editor is a superset
 * of the default behaviour.
 */
const buildAutoGrid = (designCount, tileWIn, tileHIn, cutlineShape) => {
  const usableW = PAGE_W_IN - 2 * MARGIN_IN;
  const usableH = PAGE_H_IN - 2 * MARGIN_IN - HEADER_RESERVE_IN;
  const gutter = 0.125;
  const cols = Math.max(1, Math.floor((usableW + gutter) / (tileWIn + gutter)));
  const rows = Math.max(1, Math.floor((usableH + gutter) / (tileHIn + gutter)));
  const gridW = cols * tileWIn + (cols - 1) * gutter;
  const startX = MARGIN_IN + (usableW - gridW) / 2;
  const startY = MARGIN_IN + HEADER_RESERVE_IN;
  const tiles = [];
  let designIdx = 0;
  for (let r = 0; r < rows; r += 1) {
    for (let c = 0; c < cols; c += 1) {
      tiles.push({
        id: `tile-${r}-${c}-${Math.random().toString(36).slice(2, 8)}`,
        design_idx: designCount > 0 ? designIdx % designCount : 0,
        x_in: startX + c * (tileWIn + gutter),
        y_in: startY + r * (tileHIn + gutter),
        w_in: tileWIn,
        h_in: tileHIn,
        shape: cutlineShape || 'rectangle',
      });
      designIdx += 1;
    }
  }
  return tiles;
};

const TILE_SHAPES = [
  { id: 'rectangle', label: '□', title: 'Rectangle' },
  { id: 'rounded-rectangle', label: '▢', title: 'Rounded' },
  { id: 'circle', label: '○', title: 'Circle' },
];

export const PageMakerEditor = ({
  files,
  filePreviews = [],
  defaultSize,
  cutlineShape,
  onChange,
}) => {
  const [tileWIn, tileHIn] = useMemo(() => {
    const w = Number(defaultSize?.w) || 2;
    const h = Number(defaultSize?.h) || 2;
    return unitToInches(w, h, defaultSize?.unit);
  }, [defaultSize?.w, defaultSize?.h, defaultSize?.unit]);

  const [layout, setLayout] = useState(() =>
    buildAutoGrid(files.length || 1, tileWIn, tileHIn, cutlineShape),
  );
  const [selectedId, setSelectedId] = useState(null);

  // Re-seed the grid when the design list or default size changes, but
  // skip if the user has already customized the layout (len differs or
  // any tile has moved from its grid slot).
  const lastSeedRef = useRef({ count: files.length, w: tileWIn, h: tileHIn });
  useEffect(() => {
    const seed = lastSeedRef.current;
    const countChanged = seed.count !== files.length;
    const sizeChanged = seed.w !== tileWIn || seed.h !== tileHIn;
    if (countChanged || sizeChanged) {
      lastSeedRef.current = { count: files.length, w: tileWIn, h: tileHIn };
      setLayout(buildAutoGrid(files.length || 1, tileWIn, tileHIn, cutlineShape));
      setSelectedId(null);
    }
  }, [files.length, tileWIn, tileHIn, cutlineShape]);

  // Fire onChange whenever the layout changes — the parent persists this
  // and ships it to the backend on upload.
  useEffect(() => {
    if (typeof onChange === 'function') onChange(layout);
  }, [layout, onChange]);

  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [pxPerInch, setPxPerInch] = useState(40);

  // Fit the SVG to the container width; ResizeObserver gives us pixel-
  // perfect re-fit on any container size change.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    // eslint-disable-next-line no-undef
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = entry.contentRect.width;
        setPxPerInch(w / PAGE_W_IN);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const pageWPx = PAGE_W_IN * pxPerInch;
  const pageHPx = PAGE_H_IN * pxPerInch;

  // --- Tile drag/resize pointer handling ---
  // We use pointer events + SVG-level coord conversion so drags track
  // correctly inside scrolled / scaled viewports (no getBoundingClientRect
  // drift on Safari).
  const dragRef = useRef(null); // { id, mode: 'move'|'resize', startX_in, startY_in, startTile }

  const svgPointToInches = (clientX, clientY) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const rect = svg.getBoundingClientRect();
    const px = clientX - rect.left;
    const py = clientY - rect.top;
    return { x: px / pxPerInch, y: py / pxPerInch };
  };

  const startDrag = (e, tile, mode) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedId(tile.id);
    const { x, y } = svgPointToInches(e.clientX, e.clientY);
    dragRef.current = {
      id: tile.id,
      mode,
      pointerX_in: x,
      pointerY_in: y,
      startTile: { ...tile },
    };
    // Pointer capture ensures mouseup fires even if the cursor leaves the SVG
    if (e.target && e.target.setPointerCapture) {
      try {
        e.target.setPointerCapture(e.pointerId);
      } catch (captureErr) {
        // Pointer capture occasionally throws on stale elements (element
        // was unmounted between pointerdown and this call) — non-fatal,
        // drag still works via document-level mousemove.
        log.debug('[PageMakerEditor] setPointerCapture failed:', captureErr?.name || captureErr);
      }
    }
  };

  const onPointerMove = (e) => {
    const drag = dragRef.current;
    if (!drag) return;
    const { x, y } = svgPointToInches(e.clientX, e.clientY);
    const dx = x - drag.pointerX_in;
    const dy = y - drag.pointerY_in;
    setLayout((prev) =>
      prev.map((t) => {
        if (t.id !== drag.id) return t;
        if (drag.mode === 'move') {
          const newX = clamp(
            drag.startTile.x_in + dx,
            0,
            PAGE_W_IN - drag.startTile.w_in,
          );
          const newY = clamp(
            drag.startTile.y_in + dy,
            0,
            PAGE_H_IN - drag.startTile.h_in,
          );
          return { ...t, x_in: newX, y_in: newY };
        }
        // resize (bottom-right handle)
        const newW = clamp(drag.startTile.w_in + dx, 0.5, PAGE_W_IN - drag.startTile.x_in);
        const newH = clamp(drag.startTile.h_in + dy, 0.5, PAGE_H_IN - drag.startTile.y_in);
        return { ...t, w_in: newW, h_in: newH };
      }),
    );
  };

  const endDrag = () => {
    dragRef.current = null;
  };

  // --- Tile mutations ---
  const setTileShape = (id, shape) => {
    setLayout((prev) => prev.map((t) => (t.id === id ? { ...t, shape } : t)));
  };
  const deleteTile = (id) => {
    setLayout((prev) => prev.filter((t) => t.id !== id));
    setSelectedId(null);
  };
  const addTile = () => {
    // New tile inherits the selected tile's design+size when possible,
    // or falls back to the first design + default size.
    const selected = layout.find((t) => t.id === selectedId);
    const baseIdx = selected?.design_idx ?? 0;
    const baseW = selected?.w_in ?? tileWIn;
    const baseH = selected?.h_in ?? tileHIn;
    const x = clamp(
      (selected?.x_in ?? MARGIN_IN) + 0.25,
      0,
      PAGE_W_IN - baseW,
    );
    const y = clamp(
      (selected?.y_in ?? (MARGIN_IN + HEADER_RESERVE_IN)) + 0.25,
      0,
      PAGE_H_IN - baseH,
    );
    const newTile = {
      id: `tile-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      design_idx: (baseIdx + 1) % Math.max(1, files.length),
      x_in: x,
      y_in: y,
      w_in: baseW,
      h_in: baseH,
      shape: selected?.shape || cutlineShape || 'rectangle',
    };
    setLayout((prev) => [...prev, newTile]);
    setSelectedId(newTile.id);
  };
  const cycleDesign = (id) => {
    if (files.length <= 1) return;
    setLayout((prev) =>
      prev.map((t) =>
        t.id === id ? { ...t, design_idx: (t.design_idx + 1) % files.length } : t,
      ),
    );
  };
  const resetAutoGrid = () => {
    setLayout(buildAutoGrid(files.length || 1, tileWIn, tileHIn, cutlineShape));
    setSelectedId(null);
  };

  const selectedTile = layout.find((t) => t.id === selectedId) || null;

  // Preview URLs for the design thumbnails the parent pre-rendered.
  const previewForIdx = (idx) => filePreviews.find((p) => p.idx === idx)?.thumbUrl || null;

  return (
    <div
      className="mb-6 rounded-lg border-2 border-[#4A7C59]/40 bg-white"
      data-testid="page-maker-editor"
    >
      {/* Header / toolbar */}
      <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-[#4A7C59]/20 bg-gradient-to-r from-[#4A7C59]/10 to-emerald-50">
        <Move className="w-4 h-4 text-[#4A7C59]" />
        <h3 className="font-bold text-[#2E5A3D] text-sm">
          Freeform Layout Editor
        </h3>
        <span className="text-xs text-[#3B5F46]/70 ml-1">
          Drag tiles to position · drag the bottom-right handle to resize
        </span>
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={addTile}
            disabled={files.length === 0}
            data-testid="page-maker-add-tile"
            className="inline-flex items-center gap-1 text-xs font-semibold bg-[#4A7C59] text-white hover:bg-[#3D6A4A] disabled:opacity-40 disabled:cursor-not-allowed rounded-full px-3 py-1 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            Add Tile
          </button>
          <button
            type="button"
            onClick={resetAutoGrid}
            data-testid="page-maker-reset"
            className="inline-flex items-center gap-1 text-xs font-semibold text-[#4A7C59] hover:bg-[#4A7C59]/10 rounded-full px-3 py-1 transition-colors border border-[#4A7C59]/40"
          >
            <Grid3x3 className="w-3.5 h-3.5" />
            Auto-Grid
          </button>
        </div>
      </div>

      <div className="p-4 grid grid-cols-1 md:grid-cols-12 gap-4">
        {/* SVG sheet */}
        <div ref={containerRef} className="md:col-span-9 relative">
          <svg
            ref={svgRef}
            width={pageWPx}
            height={pageHPx}
            viewBox={`0 0 ${PAGE_W_IN} ${PAGE_H_IN}`}
            style={{ touchAction: 'none' }}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            onPointerLeave={endDrag}
            className="bg-white shadow-inner rounded border border-gray-300 select-none"
            data-testid="page-maker-svg"
          >
            {/* Safe-area margin — soft dashed border */}
            <rect
              x={MARGIN_IN}
              y={MARGIN_IN}
              width={PAGE_W_IN - 2 * MARGIN_IN}
              height={PAGE_H_IN - 2 * MARGIN_IN}
              fill="none"
              stroke="#d4d4d8"
              strokeWidth={0.02}
              strokeDasharray="0.08 0.05"
            />
            {/* Header reservation band */}
            <rect
              x={0}
              y={0}
              width={PAGE_W_IN}
              height={MARGIN_IN + HEADER_RESERVE_IN - 0.1}
              fill="#4A7C59"
              opacity={0.05}
            />
            <text
              x={PAGE_W_IN / 2}
              y={MARGIN_IN + 0.35}
              fontSize={0.18}
              fill="#4A7C59"
              opacity={0.55}
              textAnchor="middle"
            >
              Print Header · kept clear for production info
            </text>

            {/* Tiles */}
            {layout.map((t) => {
              const isSelected = t.id === selectedId;
              const hue = hueFor(t.design_idx);
              const thumb = previewForIdx(t.design_idx);
              const cx = t.x_in + t.w_in / 2;
              const cy = t.y_in + t.h_in / 2;
              return (
                <g key={t.id} data-testid={`page-maker-tile-${t.design_idx}`}>
                  {/* Tile body (clickable + draggable) */}
                  <g
                    onPointerDown={(e) => startDrag(e, t, 'move')}
                    onDoubleClick={() => cycleDesign(t.id)}
                    style={{ cursor: 'move' }}
                  >
                    {thumb ? (
                      <image
                        href={thumb}
                        x={t.x_in}
                        y={t.y_in}
                        width={t.w_in}
                        height={t.h_in}
                        preserveAspectRatio="xMidYMid slice"
                        opacity={0.92}
                      />
                    ) : (
                      <rect
                        x={t.x_in}
                        y={t.y_in}
                        width={t.w_in}
                        height={t.h_in}
                        fill={`hsl(${hue}, 55%, 78%)`}
                      />
                    )}
                    {/* Cutline shape overlay */}
                    {t.shape === 'circle' && (
                      <circle
                        cx={cx}
                        cy={cy}
                        r={Math.min(t.w_in, t.h_in) / 2}
                        fill="none"
                        stroke="#E91E63"
                        strokeWidth={0.02}
                        strokeDasharray="0.08 0.05"
                      />
                    )}
                    {t.shape === 'rounded-rectangle' && (
                      <rect
                        x={t.x_in}
                        y={t.y_in}
                        width={t.w_in}
                        height={t.h_in}
                        rx={Math.min(t.w_in, t.h_in) * 0.15}
                        fill="none"
                        stroke="#E91E63"
                        strokeWidth={0.02}
                        strokeDasharray="0.08 0.05"
                      />
                    )}
                    {(t.shape === 'rectangle' || !t.shape) && (
                      <rect
                        x={t.x_in}
                        y={t.y_in}
                        width={t.w_in}
                        height={t.h_in}
                        fill="none"
                        stroke="#E91E63"
                        strokeWidth={0.02}
                        strokeDasharray="0.08 0.05"
                      />
                    )}
                    {/* Design label */}
                    <text
                      x={cx}
                      y={cy}
                      fontSize={Math.min(t.w_in, t.h_in) * 0.2}
                      fill="#2E5A3D"
                      textAnchor="middle"
                      dominantBaseline="central"
                      fontWeight="bold"
                      pointerEvents="none"
                    >
                      #{t.design_idx + 1}
                    </text>
                  </g>

                  {/* Selection ring */}
                  {isSelected && (
                    <rect
                      x={t.x_in - 0.03}
                      y={t.y_in - 0.03}
                      width={t.w_in + 0.06}
                      height={t.h_in + 0.06}
                      fill="none"
                      stroke="#4A7C59"
                      strokeWidth={0.03}
                      pointerEvents="none"
                    />
                  )}

                  {/* Resize handle — bottom-right */}
                  {isSelected && (
                    <rect
                      x={t.x_in + t.w_in - 0.12}
                      y={t.y_in + t.h_in - 0.12}
                      width={0.24}
                      height={0.24}
                      fill="#4A7C59"
                      stroke="white"
                      strokeWidth={0.02}
                      style={{ cursor: 'nwse-resize' }}
                      onPointerDown={(e) => startDrag(e, t, 'resize')}
                      data-testid={`page-maker-tile-resize-${t.design_idx}`}
                    />
                  )}
                </g>
              );
            })}
          </svg>
        </div>

        {/* Side panel: selected tile controls + legend */}
        <div className="md:col-span-3 flex flex-col gap-3">
          <div className="rounded-md border border-gray-200 p-3 bg-gray-50">
            <div className="text-xs font-semibold text-gray-700 mb-2">
              {selectedTile ? `Tile #${selectedTile.design_idx + 1}` : 'Select a tile'}
            </div>
            {selectedTile ? (
              <div className="space-y-3">
                <div>
                  <div className="text-[10px] uppercase text-gray-500 mb-1">Cut Shape</div>
                  <div className="flex gap-1">
                    {TILE_SHAPES.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => setTileShape(selectedTile.id, s.id)}
                        title={s.title}
                        data-testid={`page-maker-shape-${s.id}`}
                        className={`flex-1 text-sm font-bold rounded border py-1 transition-colors ${
                          selectedTile.shape === s.id
                            ? 'bg-[#4A7C59] text-white border-[#4A7C59]'
                            : 'bg-white text-gray-600 border-gray-300 hover:border-[#4A7C59]'
                        }`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-gray-500 mb-1">Design</div>
                  <button
                    type="button"
                    onClick={() => cycleDesign(selectedTile.id)}
                    disabled={files.length <= 1}
                    data-testid="page-maker-cycle-design"
                    className="w-full text-xs bg-white border border-gray-300 hover:border-[#4A7C59] rounded px-2 py-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    #{selectedTile.design_idx + 1} of {files.length || 1} →
                  </button>
                </div>
                <div className="text-[10px] text-gray-500 leading-tight">
                  {selectedTile.w_in.toFixed(2)}" × {selectedTile.h_in.toFixed(2)}"
                  <br />
                  @ ({selectedTile.x_in.toFixed(2)}", {selectedTile.y_in.toFixed(2)}")
                </div>
                <button
                  type="button"
                  onClick={() => deleteTile(selectedTile.id)}
                  data-testid="page-maker-delete-tile"
                  className="w-full inline-flex items-center justify-center gap-1 text-xs text-red-600 hover:bg-red-50 border border-red-300 rounded px-2 py-1.5 transition-colors"
                >
                  <Trash2 className="w-3 h-3" />
                  Delete Tile
                </button>
              </div>
            ) : (
              <p className="text-xs text-gray-500">
                Click any tile on the sheet to edit its shape, design, or size.
              </p>
            )}
          </div>

          <div className="rounded-md border border-gray-200 p-3 bg-gray-50">
            <div className="text-[10px] uppercase text-gray-500 mb-1">Designs on sheet</div>
            <div className="text-xs text-gray-700">
              {layout.length} tiles · {files.length || 0} unique design{files.length === 1 ? '' : 's'}
            </div>
            <div className="mt-2 text-[10px] text-gray-500 leading-relaxed">
              Dashed magenta = cutline (won&apos;t print). Double-click a
              tile to cycle through your uploaded designs.
            </div>
          </div>
          <button
            type="button"
            onClick={resetAutoGrid}
            data-testid="page-maker-reset-side"
            className="inline-flex items-center justify-center gap-2 text-xs font-semibold text-[#4A7C59] hover:bg-[#4A7C59]/10 rounded px-2 py-2 transition-colors border border-[#4A7C59]/40"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Reset to Auto-Grid
          </button>
        </div>
      </div>
    </div>
  );
};

export default PageMakerEditor;
